import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import {
  ITEM_NAMES,
  type ItemName,
  PROJECT_NAMES,
  type ProjectName,
} from "product-test-types";

import { getItemName } from "./get-plural-of-item-name";
import { getProjectName } from "./get-plural-of-project-name";

type Gender = "masculine" | "feminine" | "neuter";
type EntityType = "item" | "project";

// Map known entity keys (english singular keys from itemNames / projectNames)
// to German grammatical gender.
// Extend this map if new item types are introduced.
const GERMAN_GENDERS: Record<string, Gender> = {
  Project: "neuter",
  Company: "neuter",
  Subsidiary: "feminine",
  Department: "feminine",
  OE: "feminine",
  Milestone: "masculine",
  Measure: "feminine",
  Deliverable: "neuter",
  Finding: "masculine",
  Task: "feminine",
  Activity: "feminine",
  "Tracking Item": "neuter",
  Initiative: "feminine",
  Question: "feminine",
};

const GERMAN_NEW_PREFIX: Record<Gender, string> = {
  masculine: "Neuen",
  feminine: "Neue",
  neuter: "Neues",
};

function isItemName(entityNameKey?: string): entityNameKey is ItemName {
  return ITEM_NAMES.some(({ single }) => single === entityNameKey);
}

function isProjectName(entityNameKey?: string): entityNameKey is ProjectName {
  return PROJECT_NAMES.some(({ single }) => single === entityNameKey);
}

function getEntityType(
  entityNameKey?: string,
  entityType?: EntityType
): EntityType | "unknown" {
  if (entityType) {
    return entityType;
  }

  if (isItemName(entityNameKey)) {
    return "item";
  }

  if (isProjectName(entityNameKey)) {
    return "project";
  }

  return "unknown";
}

function getLocalizedEntityName(
  language: SupportedLanguage,
  entityNameKey?: string,
  entityType?: EntityType
): string {
  const resolvedEntityType = getEntityType(entityNameKey, entityType);

  if (!entityNameKey) {
    if (resolvedEntityType === "project") {
      return getLocalizedMessage(language, "project") || "Project";
    }

    if (resolvedEntityType === "item") {
      return getLocalizedMessage(language, "trackingItem") || "Tracking Item";
    }

    return "";
  }

  if (resolvedEntityType === "item") {
    if (!isItemName(entityNameKey)) {
      return entityNameKey;
    }
    return getItemName(entityNameKey as ItemName, language) || entityNameKey;
  }

  if (resolvedEntityType === "project") {
    if (!isProjectName(entityNameKey)) {
      return entityNameKey;
    }
    return (
      getProjectName(entityNameKey as ProjectName, language) || entityNameKey
    );
  }

  return entityNameKey;
}

function getGermanAddNewLabel(
  entityNameKey: string,
  localizedEntityName: string
) {
  const gender = GERMAN_GENDERS[entityNameKey] || "feminine";
  const prefix = GERMAN_NEW_PREFIX[gender];

  return `${prefix} ${localizedEntityName} hinzufügen`;
}

export function getAddNewLabel(
  language?: SupportedLanguage,
  entityNameKey?: string,
  entityType?: EntityType
): string {
  const lang = language || "en";
  const key =
    entityNameKey || (entityType === "project" ? "Project" : "Tracking Item");
  const localizedEntityName =
    getLocalizedEntityName(lang, key, entityType) || key;

  if (lang === "de") {
    return getGermanAddNewLabel(key, localizedEntityName);
  }

  const addNew = getLocalizedMessage(lang, "addNew") || "Add New";
  return `${addNew} ${localizedEntityName}`;
}

export function getAddProjectButtonLabel(language?: SupportedLanguage): string {
  return (
    getLocalizedMessage(language || "en", "addProjectShort") || "Add Project"
  );
}

export function getCreateEntityLabel(
  language?: SupportedLanguage,
  entityNameKey?: string,
  entityType?: EntityType
): string {
  const lang = language || "en";
  const key =
    entityNameKey || (entityType === "project" ? "Project" : "Tracking Item");
  const resolvedEntityType = getEntityType(key, entityType);
  const localizedEntityName =
    getLocalizedEntityName(lang, key, entityType) || key;

  if (lang === "de") {
    return getGermanAddNewLabel(key, localizedEntityName);
  }

  if (resolvedEntityType === "project") {
    return (
      getLocalizedMessage(lang, "createProject", {
        projectName: localizedEntityName,
      }) || `Create ${localizedEntityName}`
    );
  }

  if (resolvedEntityType === "item") {
    return (
      getLocalizedMessage(lang, "createTrackingItem", {
        itemName: localizedEntityName,
      }) || `Create ${localizedEntityName}`
    );
  }

  return `Create ${localizedEntityName}`;
}

export function getEditEntityLabel(
  language?: SupportedLanguage,
  entityNameKey?: string,
  entityType?: EntityType
): string {
  const lang = language || "en";
  const key =
    entityNameKey || (entityType === "project" ? "Project" : "Tracking Item");
  const resolvedEntityType = getEntityType(key, entityType);
  const localizedEntityName =
    getLocalizedEntityName(lang, key, entityType) || key;

  if (resolvedEntityType === "project") {
    return (
      getLocalizedMessage(lang, "editProject", {
        projectName: localizedEntityName,
      }) || `Edit ${localizedEntityName}`
    );
  }

  if (resolvedEntityType === "item") {
    return (
      getLocalizedMessage(lang, "editTrackingItem", {
        itemName: localizedEntityName,
      }) || `Edit ${localizedEntityName}`
    );
  }

  return `Edit ${localizedEntityName}`;
}
